/**
 * ESS POS — Stage 3: Waiter Workspace
 *
 * Implements:
 * 1. Waiter Dashboard (Assigned tables, available/occupied counts, ready orders, attention alerts)
 * 2. Visual Restaurant Table Layout (Available, Occupied, Ordering, Preparing, Ready, Awaiting Payment)
 * 3. Order Creation (Table selection, guest count, category browse, search, item notes, submit to kitchen)
 * 4. Order Tracking & Live Kitchen Status (Auto-polling status updates + strict role restrictions:
 *    Waiters can Request Bill or Request Manager Cancellation, but NEVER process payments or change prices)
 */

import { apiClient } from '../api/apiClient';
import { restaurantMockAdapter } from '../api/mockAdapter';
import {
  escapeHtml,
  formatCurrency,
  formatTimeShort,
  openModal,
  renderEmptyState,
  renderErrorState,
  renderLoadingState,
  showToast,
} from '../components/ui';
import {
  CategoryRecord,
  ProductRecord,
  RestaurantOrder,
  RestaurantTable,
  TableStatus,
} from '../types/contracts';

interface WaiterOrderDraftLine {
  variantId: string;
  productName: string;
  sku: string;
  unitPriceCents: number;
  maxStock: number;
  quantity: number;
  notes: string;
}

const waiterDraftState: {
  selectedTableId: string;
  guests: number;
  selectedCategoryId: string;
  searchQuery: string;
  zoneFilter: string;
  lines: WaiterOrderDraftLine[];
} = {
  selectedTableId: '',
  guests: 2,
  selectedCategoryId: 'ALL',
  searchQuery: '',
  zoneFilter: 'ALL',
  lines: [],
};

export async function renderWaiterWorkspace(
  viewport: HTMLElement,
  activeTab: string,
  onNavigateTab: (tabId: string) => void
) {
  viewport.innerHTML = renderLoadingState('Loading Restaurant Floor & Kitchen Status...');

  try {
    const [categories, products] = await Promise.all([
      apiClient.listCategories(),
      apiClient.listProducts(),
    ]);

    const tenantId = apiClient.getEffectiveTenantId();
    const user = apiClient.getUser();
    const waiterFullName = `${user?.firstName || 'Kevin'} ${user?.lastName || 'Omondi'}`.trim();

    const tables = restaurantMockAdapter.listTables(tenantId);
    const orders = restaurantMockAdapter.listOrders(tenantId);

    if (!waiterDraftState.selectedTableId && tables.length > 0) {
      waiterDraftState.selectedTableId = tables[0].id;
    }

    if (activeTab === 'profile') {
      renderWaiterProfileScreen(viewport, {
        tenantId,
        user,
        waiterFullName,
        tables,
        orders,
        onNavigateTab,
        onRefresh: () => renderWaiterWorkspace(viewport, 'profile', onNavigateTab),
      });
    } else if (activeTab === 'dashboard') {
      renderWaiterDashboard(viewport, {
        tenantId,
        tables,
        orders,
        waiterFullName,
        onNavigateTab,
      });
    } else if (activeTab === 'order-create') {
      renderWaiterOrderCreation(viewport, {
        tenantId,
        waiterFullName,
        tables,
        categories,
        products,
        onNavigateTab,
      });
    } else if (activeTab === 'kitchen-tracking') {
      renderKitchenTrackingScreen(viewport, {
        tenantId,
        waiterFullName,
        orders,
        onRefresh: () => renderWaiterWorkspace(viewport, 'kitchen-tracking', onNavigateTab),
      });
    } else {
      // Default: 'tables'
      renderFloorTableLayout(viewport, {
        tenantId,
        waiterFullName,
        tables,
        orders,
        onNavigateTab,
        onRefresh: () => renderWaiterWorkspace(viewport, 'tables', onNavigateTab),
      });
    }
  } catch (err: any) {
    viewport.innerHTML = renderErrorState({
      title: 'Failed to Load Waiter Workspace',
      message: err?.message || 'Unable to load floor tables or menu catalog.',
      retryButtonId: 'waiter-retry-btn',
    });
    viewport.querySelector('#waiter-retry-btn')?.addEventListener('click', () => {
      renderWaiterWorkspace(viewport, activeTab, onNavigateTab);
    });
  }
}

function getTableStatusBadgeClass(status: TableStatus): string {
  switch (status) {
    case 'AVAILABLE':
      return 'ess-badge-success';
    case 'READY':
      return 'ess-badge-orange';
    case 'PREPARING':
    case 'ORDERING':
      return 'ess-badge-warning';
    case 'AWAITING_PAYMENT':
      return 'ess-badge-danger';
    default:
      return 'ess-badge-neutral';
  }
}

// ============================================================================
// 1. WAITER DASHBOARD
// ============================================================================

function renderWaiterDashboard(
  viewport: HTMLElement,
  params: {
    tenantId: string;
    tables: RestaurantTable[];
    orders: RestaurantOrder[];
    waiterFullName: string;
    onNavigateTab: (tab: string) => void;
  }
) {
  const availableTables = params.tables.filter((t) => t.status === 'AVAILABLE');
  const occupiedTables = params.tables.filter((t) => t.status !== 'AVAILABLE');
  const awaitingOrderTables = params.tables.filter(
    (t) => t.status === 'OCCUPIED' || t.status === 'ORDERING'
  );
  const preparingOrders = params.orders.filter(
    (o) => o.status === 'SUBMITTED' || o.status === 'PREPARING'
  );
  const readyOrders = params.orders.filter((o) => o.status === 'READY');
  const attentionOrders = params.orders.filter(
    (o) => o.status === 'READY' || o.status === 'AWAITING_PAYMENT' || o.cancellationRequested
  );

  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Waiter Service Overview &mdash; ${escapeHtml(params.waiterFullName)}</h2>
          <p class="ess-section-desc">Touch-optimized floor summary, ready kitchen orders &amp; table assignments</p>
        </div>
        <div class="ess-btn-group">
          <button type="button" class="ess-btn ess-btn-secondary" id="wd-switch-waiter">👤 Switch Waiter</button>
          <button type="button" class="ess-btn ess-btn-secondary" id="wd-view-profile">My Profile</button>
          <button type="button" class="ess-btn ess-btn-secondary" id="wd-go-tables">View Floor Map</button>
          <button type="button" class="ess-btn ess-btn-primary" id="wd-go-new-order">+ New Table Order</button>
        </div>
      </div>

      <div class="ess-kpi-grid">
        <div class="ess-card ess-kpi-card ess-kpi-highlight">
          <div class="ess-kpi-label">Orders Ready to Serve</div>
          <div class="ess-kpi-value">${readyOrders.length}</div>
          <div class="ess-kpi-meta">Pick up from kitchen pass immediately</div>
        </div>
        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">Available Tables</div>
          <div class="ess-kpi-value">${availableTables.length} / ${params.tables.length}</div>
          <div class="ess-kpi-meta">${occupiedTables.length} tables currently active</div>
        </div>
        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">Tables Awaiting Orders</div>
          <div class="ess-kpi-value">${awaitingOrderTables.length}</div>
          <div class="ess-kpi-meta">Seated guests ready to order</div>
        </div>
        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">Orders in Kitchen Prep</div>
          <div class="ess-kpi-value">${preparingOrders.length}</div>
          <div class="ess-kpi-meta">${attentionOrders.length} orders needing attention</div>
        </div>
      </div>

      <div class="ess-grid-2">
        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Orders Ready &amp; Requiring Attention</h3>
            <span class="ess-badge ess-badge-orange">${attentionOrders.length} Active</span>
          </div>
          ${
            attentionOrders.length === 0
              ? renderEmptyState({
                  title: 'All Ready Orders Served',
                  description: 'No urgent kitchen pickups or pending bill requests at this moment.',
                })
              : `
            <div class="ess-stack-sm">
              ${attentionOrders
                .map(
                  (ord) => `
                <div class="ess-alert-card ${ord.status === 'READY' ? 'ess-alert-orange' : 'ess-alert-neutral'}">
                  <div style="display:flex;justify-content:space-between;align-items:center;">
                    <strong>${escapeHtml(ord.orderNumber)} &bull; ${escapeHtml(ord.tableName)}</strong>
                    <span class="ess-badge ${ord.status === 'READY' ? 'ess-badge-orange' : 'ess-badge-warning'}">${escapeHtml(ord.status)}</span>
                  </div>
                  <p>${ord.items.map((i) => `${i.quantity}x ${i.productName}`).join(', ')}</p>
                </div>
              `
                )
                .join('')}
            </div>
          `
          }
        </div>

        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">My Assigned &amp; Active Tables</h3>
            <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="wd-open-floor">Open Floor Layout</button>
          </div>
          <div class="ess-stack-sm">
            ${occupiedTables
              .map(
                (t) => `
              <div class="ess-kv-row">
                <div>
                  <strong>${escapeHtml(t.number)} &mdash; ${escapeHtml(t.name)}</strong>
                  <div style="font-size:12px;color:var(--ess-text-muted);">${t.guests} Guests &bull; ${escapeHtml(t.zone)}</div>
                </div>
                <div style="text-align:right;">
                  <span class="ess-badge ${getTableStatusBadgeClass(t.status)}">${escapeHtml(t.status)}</span>
                  <div style="font-size:12px;font-weight:600;margin-top:2px;">${formatCurrency(t.currentBillCents)}</div>
                </div>
              </div>
            `
              )
              .join('')}
          </div>
        </div>
      </div>
    </div>
  `;

  viewport.querySelector('#wd-switch-waiter')?.addEventListener('click', () => openFastSwitchStaffModal(params.tenantId));
  viewport.querySelector('#wd-view-profile')?.addEventListener('click', () => params.onNavigateTab('profile'));
  viewport.querySelector('#wd-go-tables')?.addEventListener('click', () => params.onNavigateTab('tables'));
  viewport.querySelector('#wd-open-floor')?.addEventListener('click', () => params.onNavigateTab('tables'));
  viewport.querySelector('#wd-go-new-order')?.addEventListener('click', () => params.onNavigateTab('order-create'));
}

// ============================================================================
// 2. VISUAL RESTAURANT FLOOR / TABLE INTERFACE
// ============================================================================

function renderFloorTableLayout(
  viewport: HTMLElement,
  params: {
    tenantId: string;
    waiterFullName: string;
    tables: RestaurantTable[];
    orders: RestaurantOrder[];
    onNavigateTab: (tab: string) => void;
    onRefresh: () => void;
  }
) {
  const zones = ['ALL', 'Main Dining', 'Garden Terrace', 'VIP Lounge'];
  const filteredTables = params.tables.filter(
    (t) => waiterDraftState.zoneFilter === 'ALL' || t.zone === waiterDraftState.zoneFilter
  );

  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Restaurant Floor &amp; Table Status Map</h2>
          <p class="ess-section-desc">Tap any table to seat guests, start an order, or request the customer bill for Cashier</p>
        </div>
        <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;">
          <div class="ess-category-pills">
            ${zones
              .map(
                (z) => `
              <button type="button" class="ess-pill ${waiterDraftState.zoneFilter === z ? 'active' : ''}" data-zone-filter="${escapeHtml(z)}">
                ${z === 'ALL' ? 'All Zones' : escapeHtml(z)}
              </button>
            `
              )
              .join('')}
          </div>
          <div class="ess-btn-group">
            <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="fl-switch-waiter">👤 Switch Waiter</button>
            <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="fl-view-profile">My Waiter Profile</button>
          </div>
        </div>
      </div>

      <!-- Status Legend -->
      <div class="ess-legend-row">
        <span class="ess-badge ess-badge-success">AVAILABLE</span>
        <span class="ess-badge ess-badge-neutral">OCCUPIED</span>
        <span class="ess-badge ess-badge-warning">ORDERING / PREPARING</span>
        <span class="ess-badge ess-badge-orange">READY TO SERVE</span>
        <span class="ess-badge ess-badge-danger">AWAITING PAYMENT</span>
      </div>

      <!-- Visual Floor Grid -->
      <div class="ess-table-floor-grid">
        ${filteredTables
          .map(
            (t) => `
          <div class="ess-floor-table-card status-${t.status.toLowerCase()}" data-table-card="${escapeHtml(t.id)}">
            <div class="ess-floor-table-top">
              <span class="ess-table-number">${escapeHtml(t.number)}</span>
              <span class="ess-badge ${getTableStatusBadgeClass(t.status)}">${escapeHtml(t.status.replace('_', ' '))}</span>
            </div>
            <div class="ess-floor-table-name">${escapeHtml(t.name)}</div>
            <div class="ess-floor-table-meta">
              <span>Zone: ${escapeHtml(t.zone)}</span>
              <span>Guests: <strong>${t.guests} / ${t.capacity}</strong></span>
            </div>
            <div class="ess-floor-table-meta">
              <span>Waiter: ${escapeHtml(t.assignedWaiterName || 'Unassigned')}</span>
              <strong>${t.currentBillCents > 0 ? formatCurrency(t.currentBillCents) : 'No Active Bill'}</strong>
            </div>
            <div class="ess-floor-table-actions">
              <button type="button" class="ess-btn ess-btn-primary ess-btn-sm" data-start-order="${escapeHtml(t.id)}">
                ${t.status === 'AVAILABLE' ? 'Open & Order' : '+ Add / Edit Order'}
              </button>
              ${
                t.status !== 'AVAILABLE' && t.currentBillCents > 0
                  ? `<button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" data-req-bill="${escapeHtml(t.id)}">
                      Request Bill
                    </button>`
                  : ''
              }
              ${
                t.status !== 'AVAILABLE'
                  ? `<button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" data-fl-transfer-tbl="${escapeHtml(t.id)}" title="Transfer table to fellow waiter">
                      Transfer
                    </button>`
                  : ''
              }
            </div>
          </div>
        `
          )
          .join('')}
      </div>
    </div>
  `;

  viewport.querySelectorAll('[data-zone-filter]').forEach((btn) => {
    btn.addEventListener('click', () => {
      waiterDraftState.zoneFilter = btn.getAttribute('data-zone-filter') || 'ALL';
      renderFloorTableLayout(viewport, params);
    });
  });

  viewport.querySelectorAll('[data-start-order]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const tableId = btn.getAttribute('data-start-order') || '';
      const tbl = params.tables.find((t) => t.id === tableId);
      if (tbl) {
        if (tbl.status === 'AVAILABLE') {
          restaurantMockAdapter.updateTableStatus(params.tenantId, tbl.id, {
            status: 'ORDERING',
            guests: 2,
            assignedWaiterName: params.waiterFullName,
          });
        }
        waiterDraftState.selectedTableId = tbl.id;
        waiterDraftState.guests = tbl.guests || 2;
        params.onNavigateTab('order-create');
      }
    });
  });

  viewport.querySelectorAll('[data-req-bill]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const tableId = btn.getAttribute('data-req-bill') || '';
      const tbl = params.tables.find((t) => t.id === tableId);
      if (!tbl) return;

      restaurantMockAdapter.updateTableStatus(params.tenantId, tbl.id, {
        status: 'AWAITING_PAYMENT',
      });
      if (tbl.activeOrderId) {
        restaurantMockAdapter.advanceOrderStatus(
          params.tenantId,
          tbl.activeOrderId,
          'AWAITING_PAYMENT',
          params.waiterFullName
        );
      }
      showToast({
        title: 'Bill Sent to Cashier Workspace',
        message: `${tbl.number} (${formatCurrency(tbl.currentBillCents)}) is now queued in Cashier Pending Bills.`,
        type: 'info',
      });
      params.onRefresh();
    });
  });

  viewport.querySelector('#fl-switch-waiter')?.addEventListener('click', () => openFastSwitchStaffModal(params.tenantId));
  viewport.querySelector('#fl-view-profile')?.addEventListener('click', () => params.onNavigateTab('profile'));

  viewport.querySelectorAll('[data-fl-transfer-tbl]').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const tableId = btn.getAttribute('data-fl-transfer-tbl') || '';
      const tbl = params.tables.find((t) => t.id === tableId);
      if (tbl) {
        let staffList: any[] = [];
        try {
          staffList = await apiClient.stationStaffList(params.tenantId);
        } catch {
          staffList = await apiClient.listStaff();
        }
        openTransferTableModal(params.tenantId, tbl, staffList, params.onRefresh);
      }
    });
  });
}

// ============================================================================
// 3. WAITER ORDER CREATION
// ============================================================================

function renderWaiterOrderCreation(
  viewport: HTMLElement,
  params: {
    tenantId: string;
    waiterFullName: string;
    tables: RestaurantTable[];
    categories: CategoryRecord[];
    products: ProductRecord[];
    onNavigateTab: (tab: string) => void;
  }
) {
  const renderOrderBuilder = () => {
    const selectedTable =
      params.tables.find((t) => t.id === waiterDraftState.selectedTableId) || params.tables[0];

    const filteredProducts = params.products.filter((p) => {
      if (
        waiterDraftState.selectedCategoryId !== 'ALL' &&
        p.category_id !== waiterDraftState.selectedCategoryId
      ) {
        return false;
      }
      if (waiterDraftState.searchQuery.trim()) {
        return p.name.toLowerCase().includes(waiterDraftState.searchQuery.toLowerCase());
      }
      return true;
    });

    const draftTotalCents = waiterDraftState.lines.reduce(
      (sum, l) => sum + l.unitPriceCents * l.quantity,
      0
    );

    viewport.innerHTML = `
      <div class="ess-pos-layout">
        <!-- Left Menu Selection -->
        <div class="ess-pos-catalog">
          <div class="ess-card ess-pos-toolbar">
            <div style="display:flex;gap:10px;flex:1;flex-wrap:wrap;">
              <div style="min-width:200px;flex:1;">
                <label class="ess-label" for="waiter-table-select">1. Select Table *</label>
                <select id="waiter-table-select" class="ess-select">
                  ${params.tables
                    .map(
                      (t) => `
                    <option value="${escapeHtml(t.id)}" ${t.id === selectedTable?.id ? 'selected' : ''}>
                      ${escapeHtml(t.number)} &mdash; ${escapeHtml(t.name)} (${escapeHtml(t.status)})
                    </option>
                  `
                    )
                    .join('')}
                </select>
              </div>
              <div style="width:110px;">
                <label class="ess-label" for="waiter-guest-count">Guests</label>
                <input type="number" id="waiter-guest-count" class="ess-input" min="1" max="20" value="${waiterDraftState.guests}" />
              </div>
              <div style="flex:1.5;min-width:200px;">
                <label class="ess-label" for="waiter-menu-search">Search Menu</label>
                <input type="search" id="waiter-menu-search" class="ess-input" placeholder="Search food or drinks..." value="${escapeHtml(waiterDraftState.searchQuery)}" />
              </div>
            </div>
          </div>

          <div class="ess-category-pills">
            <button type="button" class="ess-pill ${waiterDraftState.selectedCategoryId === 'ALL' ? 'active' : ''}" data-w-cat="ALL">
              All Categories
            </button>
            ${params.categories
              .map(
                (c) => `
              <button type="button" class="ess-pill ${waiterDraftState.selectedCategoryId === c.id ? 'active' : ''}" data-w-cat="${escapeHtml(c.id)}">
                ${escapeHtml(c.name)}
              </button>
            `
              )
              .join('')}
          </div>

          <div class="ess-product-grid">
            ${filteredProducts
              .map((p) => {
                const v = p.variants[0];
                if (!v) return '';
                const stock = Number(v.total_stock ?? 0);
                const isOut = stock <= 0;
                return `
                  <div class="ess-product-card ${isOut ? 'ess-product-disabled' : ''}" data-w-add-variant="${escapeHtml(v.id)}">
                    ${
                      p.image_url
                        ? `
                      <div class="ess-product-img-wrap">
                        <img src="${escapeHtml(p.image_url)}" alt="${escapeHtml(p.name)}" class="ess-product-img" loading="lazy" onerror="this.parentElement.style.display='none';" />
                        <span class="ess-badge ${isOut ? 'ess-badge-danger' : 'ess-badge-success'} ess-product-badge-overlay">
                          ${isOut ? 'Sold Out' : `${stock} Avail`}
                        </span>
                      </div>
                    `
                        : `
                      <div class="ess-product-card-top">
                        <span class="ess-badge ${isOut ? 'ess-badge-danger' : 'ess-badge-success'}">
                          ${isOut ? 'Sold Out' : `${stock} Avail`}
                        </span>
                      </div>
                    `
                    }
                    <div class="ess-product-name">${escapeHtml(p.name)}</div>
                    <div class="ess-product-card-bottom">
                      <div class="ess-product-price">${formatCurrency(v.retail_price_cents)}</div>
                      <button type="button" class="ess-btn ${isOut ? 'ess-btn-secondary' : 'ess-btn-primary'} ess-btn-sm" ${isOut ? 'disabled' : ''}>
                        + Add to Order
                      </button>
                    </div>
                  </div>
                `;
              })
              .join('')}
          </div>
        </div>

        <!-- Right Kitchen Order Ticket (KOT) Review -->
        <aside class="ess-pos-cart">
          <div class="ess-pos-cart-header">
            <div>
              <h3 class="ess-card-title">Kitchen Order Ticket (KOT)</h3>
              <div class="ess-card-subtitle">
                Table: <strong>${escapeHtml(selectedTable ? `${selectedTable.number} (${selectedTable.name})` : '')}</strong>
              </div>
            </div>
            <span class="ess-badge ess-badge-orange">Waiter Mode</span>
          </div>

          <div class="ess-pos-cart-items">
            ${
              waiterDraftState.lines.length === 0
                ? `<div class="ess-cart-empty">
                    <p><strong>No Dishes Selected</strong></p>
                    <span>Tap food or drinks on the left to build the table order.</span>
                  </div>`
                : waiterDraftState.lines
                    .map(
                      (line, idx) => `
                  <div class="ess-cart-line">
                    <div class="ess-cart-line-main">
                      <div>
                        <div class="ess-cart-line-title">${escapeHtml(line.productName)}</div>
                        <div class="ess-cart-line-meta">${formatCurrency(line.unitPriceCents)} each (Fixed Menu Price)</div>
                      </div>
                      <div class="ess-cart-line-total">${formatCurrency(line.unitPriceCents * line.quantity)}</div>
                    </div>
                    <div class="ess-cart-line-controls">
                      <div class="ess-qty-stepper">
                        <button type="button" class="ess-qty-btn" data-w-dec="${idx}">&minus;</button>
                        <span class="ess-qty-val">${line.quantity}</span>
                        <button type="button" class="ess-qty-btn" data-w-inc="${idx}">+</button>
                      </div>
                      <input
                        type="text"
                        class="ess-input ess-input-sm"
                        placeholder="Kitchen note (e.g. No onions)..."
                        value="${escapeHtml(line.notes)}"
                        data-w-note-input="${idx}"
                        style="flex:1;"
                      />
                    </div>
                  </div>
                `
                    )
                    .join('')
            }
          </div>

          <div class="ess-pos-cart-footer">
            <div class="ess-alert-card ess-alert-neutral" style="margin-bottom:8px;padding:8px 10px;font-size:12px;">
              Waiter Restriction: Prices &amp; payment collection are locked. Submitting sends a Kitchen Order Ticket (KOT).
            </div>
            <div class="ess-pos-total-row">
              <span>Order Running Total</span>
              <strong>${formatCurrency(draftTotalCents)}</strong>
            </div>
            <button
              type="button"
              class="ess-btn ess-btn-primary ess-btn-lg ess-btn-block"
              id="waiter-submit-kot-btn"
              ${waiterDraftState.lines.length === 0 ? 'disabled' : ''}
            >
              Submit Order to Kitchen (${waiterDraftState.lines.reduce((s, i) => s + i.quantity, 0)} Items)
            </button>
          </div>
        </aside>
      </div>
    `;

    const tblSelect = viewport.querySelector('#waiter-table-select') as HTMLSelectElement | null;
    tblSelect?.addEventListener('change', () => {
      waiterDraftState.selectedTableId = tblSelect.value;
      renderOrderBuilder();
    });

    const guestInput = viewport.querySelector('#waiter-guest-count') as HTMLInputElement | null;
    guestInput?.addEventListener('change', () => {
      waiterDraftState.guests = Math.max(1, Number(guestInput.value || 1));
    });

    const searchInput = viewport.querySelector('#waiter-menu-search') as HTMLInputElement | null;
    searchInput?.addEventListener('input', () => {
      waiterDraftState.searchQuery = searchInput.value;
      renderOrderBuilder();
      const el = viewport.querySelector('#waiter-menu-search') as HTMLInputElement | null;
      el?.focus();
    });

    viewport.querySelectorAll('[data-w-cat]').forEach((btn) => {
      btn.addEventListener('click', () => {
        waiterDraftState.selectedCategoryId = btn.getAttribute('data-w-cat') || 'ALL';
        renderOrderBuilder();
      });
    });

    viewport.querySelectorAll('[data-w-add-variant]').forEach((card) => {
      card.addEventListener('click', () => {
        const vid = card.getAttribute('data-w-add-variant');
        if (!vid) return;
        for (const p of params.products) {
          const v = p.variants.find((va) => va.id === vid);
          if (v) {
            const avail = Number(v.total_stock ?? 0);
            if (avail <= 0) return;
            const existing = waiterDraftState.lines.find((l) => l.variantId === vid);
            if (existing) {
              if (existing.quantity + 1 <= avail) existing.quantity += 1;
            } else {
              waiterDraftState.lines.push({
                variantId: v.id,
                productName: p.name,
                sku: v.sku,
                unitPriceCents: v.retail_price_cents,
                maxStock: avail,
                quantity: 1,
                notes: '',
              });
            }
            renderOrderBuilder();
            break;
          }
        }
      });
    });

    viewport.querySelectorAll('[data-w-inc]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = Number(btn.getAttribute('data-w-inc'));
        const item = waiterDraftState.lines[idx];
        if (item && item.quantity < item.maxStock) {
          item.quantity += 1;
          renderOrderBuilder();
        }
      });
    });

    viewport.querySelectorAll('[data-w-dec]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = Number(btn.getAttribute('data-w-dec'));
        const item = waiterDraftState.lines[idx];
        if (!item) return;
        if (item.quantity <= 1) waiterDraftState.lines.splice(idx, 1);
        else item.quantity -= 1;
        renderOrderBuilder();
      });
    });

    viewport.querySelectorAll('[data-w-note-input]').forEach((inp) => {
      inp.addEventListener('input', () => {
        const idx = Number(inp.getAttribute('data-w-note-input'));
        if (waiterDraftState.lines[idx]) {
          waiterDraftState.lines[idx].notes = (inp as HTMLInputElement).value;
        }
      });
    });

    viewport.querySelector('#waiter-submit-kot-btn')?.addEventListener('click', () => {
      if (!selectedTable || waiterDraftState.lines.length === 0) return;
      const newOrder = restaurantMockAdapter.submitTableOrder(params.tenantId, {
        tableId: selectedTable.id,
        waiterName: params.waiterFullName,
        guests: waiterDraftState.guests,
        items: waiterDraftState.lines.map((l) => ({
          variantId: l.variantId,
          productName: l.productName,
          sku: l.sku,
          quantity: l.quantity,
          unitPriceCents: l.unitPriceCents,
          notes: l.notes,
        })),
      });

      waiterDraftState.lines = [];
      showToast({
        title: `Order ${newOrder.orderNumber} Sent to Kitchen`,
        message: `${selectedTable.number} is now in PREPARING status.`,
        type: 'success',
      });
      params.onNavigateTab('kitchen-tracking');
    });
  };

  renderOrderBuilder();
}

// ============================================================================
// 4. ORDER TRACKING & LIVE KITCHEN STATUS
// ============================================================================

function renderKitchenTrackingScreen(
  viewport: HTMLElement,
  params: {
    tenantId: string;
    waiterFullName: string;
    orders: RestaurantOrder[];
    onRefresh: () => void;
  }
) {
  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Live Kitchen Order Tracking Board</h2>
          <p class="ess-section-desc">Real-time status of submitted orders, ready dishes, served tables &amp; bill handoffs</p>
        </div>
        <button type="button" class="ess-btn ess-btn-secondary" id="kt-refresh-btn">
          Refresh Kitchen Board
        </button>
      </div>

      <div class="ess-grid-2">
        ${params.orders
          .map(
            (ord) => `
          <div class="ess-card">
            <div class="ess-card-header">
              <div>
                <h3 class="ess-card-title">${escapeHtml(ord.orderNumber)} &mdash; ${escapeHtml(ord.tableName)}</h3>
                <div class="ess-card-subtitle">
                  Submitted ${formatTimeShort(ord.submittedAt)} &bull; Waiter: ${escapeHtml(ord.waiterName)} &bull; ${ord.guests} Guests
                </div>
              </div>
              <span class="ess-badge ${
                ord.status === 'READY'
                  ? 'ess-badge-orange'
                  : ord.status === 'PREPARING' || ord.status === 'SUBMITTED'
                  ? 'ess-badge-warning'
                  : ord.status === 'PAID'
                  ? 'ess-badge-success'
                  : 'ess-badge-neutral'
              }">${escapeHtml(ord.status)}</span>
            </div>

            <div class="ess-stack-sm" style="margin-bottom:14px;">
              ${ord.items
                .map(
                  (item) => `
                <div class="ess-kv-row">
                  <div>
                    <strong>${item.quantity}&times; ${escapeHtml(item.productName)}</strong>
                    ${item.notes ? `<div style="font-size:12px;color:var(--ess-primary);">Note: ${escapeHtml(item.notes)}</div>` : ''}
                  </div>
                  <span>${formatCurrency(item.unitPriceCents * item.quantity)}</span>
                </div>
              `
                )
                .join('')}
              <div class="ess-kv-row" style="border-top:1px solid var(--ess-border);padding-top:8px;">
                <strong>Order Running Total</strong>
                <strong>${formatCurrency(ord.totalCents)}</strong>
              </div>
            </div>

            ${
              ord.cancellationRequested
                ? `<div class="ess-alert-card ess-alert-warning" style="margin-bottom:10px;">
                    <strong>Cancellation Pending Manager Approval</strong>
                    <p>Reason: ${escapeHtml(ord.cancellationReason)}</p>
                  </div>`
                : ''
            }

            <div class="ess-btn-group">
              ${
                ord.status === 'SUBMITTED'
                  ? `<button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" data-ord-advance="${escapeHtml(ord.id)}" data-next="PREPARING">
                      Mark Kitchen Preparing
                    </button>`
                  : ''
              }
              ${
                ord.status === 'PREPARING'
                  ? `<button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" data-ord-advance="${escapeHtml(ord.id)}" data-next="READY">
                      Simulate Kitchen Ready
                    </button>`
                  : ''
              }
              ${
                ord.status === 'READY'
                  ? `<button type="button" class="ess-btn ess-btn-primary ess-btn-sm" data-ord-advance="${escapeHtml(ord.id)}" data-next="SERVED">
                      Confirm Served to Table
                    </button>`
                  : ''
              }
              ${
                ord.status === 'SERVED' || ord.status === 'READY'
                  ? `<button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" data-ord-advance="${escapeHtml(ord.id)}" data-next="AWAITING_PAYMENT">
                      Request Bill for Cashier
                    </button>`
                  : ''
              }
              ${
                ord.status !== 'PAID' && ord.status !== 'CANCELLED' && !ord.cancellationRequested
                  ? `<button type="button" class="ess-btn ess-btn-ghost ess-btn-sm" style="color:var(--ess-danger-text);" data-ord-cancel-req="${escapeHtml(ord.id)}">
                      Request Cancellation
                    </button>`
                  : ''
              }
            </div>
          </div>
        `
          )
          .join('')}
      </div>
    </div>
  `;

  viewport.querySelector('#kt-refresh-btn')?.addEventListener('click', params.onRefresh);

  viewport.querySelectorAll('[data-ord-advance]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-ord-advance') || '';
      const next = btn.getAttribute('data-next') as any;
      restaurantMockAdapter.advanceOrderStatus(params.tenantId, id, next, params.waiterFullName);
      showToast({
        title: `Order Updated to ${next}`,
        message: next === 'AWAITING_PAYMENT' ? 'Bill queued in Cashier Workspace.' : 'Table status updated.',
        type: 'success',
      });
      params.onRefresh();
    });
  });

  viewport.querySelectorAll('[data-ord-cancel-req]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-ord-cancel-req') || '';
      openModal({
        title: 'Request Manager Order Cancellation',
        subtitle: 'Waiters cannot directly delete submitted kitchen orders. A Manager must approve.',
        width: 'sm',
        bodyHtml: `
          <div class="ess-form">
            <div class="ess-form-group">
              <label class="ess-label">Reason for Cancellation *</label>
              <textarea id="waiter-cancel-reason" class="ess-textarea" rows="3" placeholder="e.g. Guest changed mind before dish was plated"></textarea>
            </div>
          </div>
        `,
        footerHtml: `
          <button type="button" class="ess-btn ess-btn-secondary" id="wc-cancel">Close</button>
          <button type="button" class="ess-btn ess-btn-danger" id="wc-submit">Send Request to Manager</button>
        `,
        onMount: (root, close) => {
          root.querySelector('#wc-cancel')?.addEventListener('click', close);
          root.querySelector('#wc-submit')?.addEventListener('click', () => {
            const reason = (root.querySelector('#waiter-cancel-reason') as HTMLTextAreaElement).value.trim();
            if (reason.length < 5) {
              showToast({
                title: 'Reason Required',
                message: 'Please enter a valid explanation for the manager.',
                type: 'warning',
              });
              return;
            }
            restaurantMockAdapter.requestOrderCancellation(
              params.tenantId,
              id,
              params.waiterFullName,
              reason
            );
            close();
            showToast({
              title: 'Cancellation Request Sent',
              message: 'Queued in Manager Approvals.',
              type: 'info',
            });
            params.onRefresh();
          });
        },
      });
    });
  });
}

// ============================================================================
// 5. WAITER SERVICE PROFILE & FLOOR TEAM DIRECTORY
// ============================================================================

async function renderWaiterProfileScreen(
  viewport: HTMLElement,
  params: {
    tenantId: string;
    user: any;
    waiterFullName: string;
    tables: RestaurantTable[];
    orders: RestaurantOrder[];
    onNavigateTab: (tab: string) => void;
    onRefresh: () => void;
  }
) {
  viewport.innerHTML = renderLoadingState('Loading Waiter Profile & Floor Team...');

  let staffList: any[] = [];
  try {
    staffList = await apiClient.stationStaffList(params.tenantId);
  } catch {
    staffList = await apiClient.listStaff();
  }

  const myTables = params.tables.filter(
    (t) => t.assignedWaiterName === params.waiterFullName && t.status !== 'AVAILABLE'
  );
  const myOrders = params.orders.filter(
    (o) => o.waiterName === params.waiterFullName
  );
  const totalBilledCents = myTables.reduce((sum, t) => sum + (t.currentBillCents || 0), 0);

  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <!-- Section Header -->
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">My Waiter Service Profile &mdash; ${escapeHtml(params.waiterFullName)}</h2>
          <p class="ess-section-desc">Manage your assigned tables, fast login PIN, shift performance and access fellow floor staff</p>
        </div>
        <div class="ess-btn-group">
          <button type="button" class="ess-btn ess-btn-secondary" id="wp-lock-btn" style="font-weight:700;">
            🔒 Lock Station
          </button>
          <button type="button" class="ess-btn ess-btn-secondary" id="wp-change-pin-btn">
            Change 4-Digit PIN
          </button>
          <button type="button" class="ess-btn ess-btn-primary" id="wp-switch-waiter-btn">
            👤 Switch Staff
          </button>
        </div>
      </div>

      <!-- Top Profile Overview & Performance Grid -->
      <div class="ess-grid-2">
        <!-- Profile Identity Card -->
        <div class="ess-card">
          <div class="ess-card-header">
            <div style="display:flex;align-items:center;gap:12px;">
              <div class="ess-staff-avatar" style="width:48px;height:48px;font-size:18px;background:var(--ess-primary);color:#fff;">
                ${escapeHtml((params.user?.firstName?.[0] || 'W') + (params.user?.lastName?.[0] || 'S'))}
              </div>
              <div>
                <h3 class="ess-card-title" style="font-size:18px;">${escapeHtml(params.waiterFullName)}</h3>
                <span class="ess-badge ess-badge-orange">Floor Waiter &bull; Active Shift</span>
              </div>
            </div>
            <span class="ess-badge ess-badge-success">&bull; On Duty</span>
          </div>

          <div class="ess-stack-sm" style="margin-top:14px;">
            <div class="ess-kv-row"><span>Work Email</span><strong>${escapeHtml(params.user?.email || 'waiter@restaurant.co.ke')}</strong></div>
            <div class="ess-kv-row"><span>Phone Number</span><strong>${escapeHtml(params.user?.phone || 'Not Set')}</strong></div>
            <div class="ess-kv-row"><span>Assigned Branch</span><strong>Main Dining Hall</strong></div>
            <div class="ess-kv-row">
              <span>Fast Login PIN</span>
              <div style="display:flex;align-items:center;gap:8px;">
                <strong style="letter-spacing:3px;">&bull;&bull;&bull;&bull;</strong>
                <button type="button" class="ess-btn ess-btn-ghost ess-btn-sm" id="wp-edit-pin" style="color:var(--ess-primary);font-size:11px;">Update PIN</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Shift Performance KPIs -->
        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">My Shift Performance Today</h3>
            <span style="font-size:12px;color:var(--ess-text-muted);">Real-time floor stats</span>
          </div>

          <div class="ess-grid-2" style="gap:12px;margin-top:10px;">
            <div class="ess-card ess-kpi-card ess-kpi-highlight" style="padding:16px;">
              <div class="ess-kpi-label">Active Tables</div>
              <div class="ess-kpi-value">${myTables.length}</div>
              <div class="ess-kpi-meta">Currently seated</div>
            </div>
            <div class="ess-card ess-kpi-card" style="padding:16px;">
              <div class="ess-kpi-label">Kitchen Orders</div>
              <div class="ess-kpi-value">${myOrders.length}</div>
              <div class="ess-kpi-meta">Submitted today</div>
            </div>
          </div>

          <div class="ess-kv-row" style="margin-top:14px;padding-top:10px;border-top:1px solid var(--ess-border);">
            <span>Active Bill Total:</span>
            <strong style="font-size:16px;color:var(--ess-primary-active);">${formatCurrency(totalBilledCents)}</strong>
          </div>
        </div>
      </div>

      <!-- My Active Tables Section -->
      <div class="ess-card">
        <div class="ess-card-header">
          <h3 class="ess-card-title">My Assigned Floor Tables (${myTables.length})</h3>
          <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="wp-goto-tables">Floor Layout</button>
        </div>
        ${
          myTables.length === 0
            ? '<div style="padding:24px;text-align:center;color:var(--ess-text-muted);">You currently have no active tables assigned. Tap "Floor Layout" to seat guests.</div>'
            : `
          <div class="ess-grid-3" style="gap:12px;margin-top:10px;">
            ${myTables
              .map(
                (t) => `
              <div class="ess-card" style="border:1.5px solid var(--ess-border);">
                <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
                  <strong>${escapeHtml(t.number)} &mdash; ${escapeHtml(t.name)}</strong>
                  <span class="ess-badge ${getTableStatusBadgeClass(t.status)}">${escapeHtml(t.status)}</span>
                </div>
                <div class="ess-kv-row"><span>Zone:</span><span>${escapeHtml(t.zone)}</span></div>
                <div class="ess-kv-row"><span>Guests:</span><span>${t.guests} / ${t.capacity}</span></div>
                <div class="ess-kv-row"><span>Current Bill:</span><strong>${formatCurrency(t.currentBillCents)}</strong></div>
                <div style="margin-top:10px;display:flex;gap:6px;">
                  <button type="button" class="ess-btn ess-btn-primary ess-btn-sm" style="flex:1;" data-wp-manage-table="${escapeHtml(t.id)}">Add Items</button>
                  <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" data-wp-transfer-table="${escapeHtml(t.id)}">Transfer</button>
                </div>
              </div>
            `
              )
              .join('')}
          </div>
        `
        }
      </div>

      <!-- Fellow Floor Staff & Users Access -->
      <div class="ess-card">
        <div class="ess-card-header">
          <div>
            <h3 class="ess-card-title">Floor Team &amp; Active Staff Directory (${staffList.length})</h3>
            <p style="font-size:12px;color:var(--ess-text-secondary);margin:0;">Fellow waiters, cashiers, managers &amp; stock keepers on this station</p>
          </div>
        </div>

        <div class="ess-grid-3" style="gap:12px;margin-top:12px;">
          ${staffList
            .map((s) => {
              const isCurrent = s.id === params.user?.id || s.email === params.user?.email;
              const initials = (s.firstName?.[0] || 'S') + (s.lastName?.[0] || 'T');
              return `
              <div class="ess-card" style="border:1.5px solid ${isCurrent ? 'var(--ess-primary)' : 'var(--ess-border)'};background:${isCurrent ? 'var(--ess-primary-soft)' : 'var(--ess-bg-main)'};">
                <div style="display:flex;align-items:center;gap:10px;margin-bottom:8px;">
                  <div class="ess-staff-avatar" style="width:36px;height:36px;font-size:13px;border-radius:50%;">
                    ${escapeHtml(initials.toUpperCase())}
                  </div>
                  <div style="flex:1;">
                    <div style="font-weight:700;font-size:13px;color:var(--ess-text-primary);">
                      ${escapeHtml(s.firstName)} ${escapeHtml(s.lastName)}
                      ${isCurrent ? '<span style="font-size:10px;color:var(--ess-primary);margin-left:4px;">(You)</span>' : ''}
                    </div>
                    <span class="ess-badge ess-badge-neutral" style="font-size:10px;padding:1px 6px;">
                      ${escapeHtml(s.primaryRole || s.roles?.[0] || 'STAFF')}
                    </span>
                  </div>
                </div>

                <div style="font-size:11px;color:var(--ess-text-muted);margin-bottom:10px;">
                  <div>${escapeHtml(s.email)}</div>
                  ${s.phone ? `<div>${escapeHtml(s.phone)}</div>` : ''}
                </div>

                ${
                  !isCurrent
                    ? `
                  <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm ess-btn-block" data-wp-switch-to="${escapeHtml(s.id)}" data-wp-name="${escapeHtml(s.firstName + ' ' + s.lastName)}">
                    👤 Switch to ${escapeHtml(s.firstName)}
                  </button>
                `
                    : '<div style="text-align:center;font-size:11px;font-weight:600;color:var(--ess-primary);padding:4px;">Currently Active</div>'
                }
              </div>
            `;
            })
            .join('')}
        </div>
      </div>
    </div>
  `;

  // Bind Events
  viewport.querySelector('#wp-lock-btn')?.addEventListener('click', () => openLockStationModal(params.user, params.tenantId));
  viewport.querySelector('#wp-change-pin-btn')?.addEventListener('click', () => openChangePinModal(params.onRefresh));
  viewport.querySelector('#wp-edit-pin')?.addEventListener('click', () => openChangePinModal(params.onRefresh));
  viewport.querySelector('#wp-switch-waiter-btn')?.addEventListener('click', () => openFastSwitchStaffModal(params.tenantId));
  viewport.querySelector('#wp-goto-tables')?.addEventListener('click', () => params.onNavigateTab('tables'));

  // Table management buttons
  viewport.querySelectorAll('[data-wp-manage-table]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tableId = btn.getAttribute('data-wp-manage-table') || '';
      waiterDraftState.selectedTableId = tableId;
      params.onNavigateTab('order-create');
    });
  });

  // Table transfer buttons
  viewport.querySelectorAll('[data-wp-transfer-table]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tableId = btn.getAttribute('data-wp-transfer-table') || '';
      const table = params.tables.find((t) => t.id === tableId);
      if (table) {
        openTransferTableModal(params.tenantId, table, staffList, params.onRefresh);
      }
    });
  });

  // Switch to specific user
  viewport.querySelectorAll('[data-wp-switch-to]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const userId = btn.getAttribute('data-wp-switch-to') || '';
      const userName = btn.getAttribute('data-wp-name') || '';
      openDirectPinSwitchModal(params.tenantId, userId, userName);
    });
  });
}

// ============================================================================
// MODALS: CHANGE PIN, TRANSFER TABLE, FAST SWITCH WAITER
// ============================================================================

export function openChangePinModal(onSuccess?: () => void) {
  openModal({
    title: 'Update My Fast Login PIN',
    subtitle: 'Enter a 4-digit numeric PIN for 1-second sign-in on POS terminals',
    width: 'sm',
    bodyHtml: `
      <form id="change-pin-form" class="ess-form">
        <div id="pin-error-box" class="ess-form-error" style="display:none;margin-bottom:12px;"></div>
        <div class="ess-form-group">
          <label class="ess-label">New 4-Digit PIN *</label>
          <input
            type="password"
            id="new-pin-input"
            class="ess-input"
            maxlength="6"
            placeholder="e.g. 5678"
            required
            style="font-size:20px;letter-spacing:6px;text-align:center;"
            autocomplete="off"
          />
          <span class="ess-field-hint">Must be 4 to 6 numeric digits</span>
        </div>
      </form>
    `,
    footerHtml: `
      <button type="button" class="ess-btn ess-btn-secondary" id="pin-cancel-btn">Cancel</button>
      <button type="submit" form="change-pin-form" class="ess-btn ess-btn-primary" id="pin-save-btn">Update PIN</button>
    `,
    onMount: (modalRoot, close) => {
      modalRoot.querySelector('#pin-cancel-btn')?.addEventListener('click', close);
      modalRoot.querySelector('#change-pin-form')?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const pin = (modalRoot.querySelector('#new-pin-input') as HTMLInputElement).value.trim();
        if (!/^\d{4,6}$/.test(pin)) {
          const errBox = modalRoot.querySelector('#pin-error-box') as HTMLElement;
          errBox.textContent = 'Please enter exactly 4 to 6 numeric digits.';
          errBox.style.display = 'block';
          return;
        }
        try {
          await apiClient.updateStaffPin(pin);
          showToast({ title: 'PIN Updated', message: 'Your fast login PIN has been updated successfully.', type: 'success' });
          close();
          if (onSuccess) onSuccess();
        } catch (err: any) {
          const errBox = modalRoot.querySelector('#pin-error-box') as HTMLElement;
          errBox.textContent = err?.message || 'Failed to update PIN.';
          errBox.style.display = 'block';
        }
      });
    },
  });
}

export function openTransferTableModal(
  tenantId: string,
  table: RestaurantTable,
  staffList: any[],
  onSuccess: () => void
) {
  const otherWaiters = staffList.filter((s) => s.roles.includes('WAITER') || s.primaryRole === 'WAITER');
  openModal({
    title: `Transfer Table ${escapeHtml(table.number)}`,
    subtitle: `Reassign ${escapeHtml(table.name)} to a fellow waiter on floor duty`,
    width: 'sm',
    bodyHtml: `
      <div class="ess-form">
        <div class="ess-form-group">
          <label class="ess-label">Select Waiter to Hand Over Table *</label>
          <select id="transfer-waiter-select" class="ess-select">
            ${otherWaiters.map((w) => `<option value="${escapeHtml(w.firstName + ' ' + w.lastName)}">${escapeHtml(w.firstName + ' ' + w.lastName)} (${escapeHtml(w.email)})</option>`).join('')}
          </select>
        </div>
      </div>
    `,
    footerHtml: `
      <button type="button" class="ess-btn ess-btn-secondary" id="transfer-cancel">Cancel</button>
      <button type="button" class="ess-btn ess-btn-primary" id="transfer-confirm">Transfer Table</button>
    `,
    onMount: (modalRoot, close) => {
      modalRoot.querySelector('#transfer-cancel')?.addEventListener('click', close);
      modalRoot.querySelector('#transfer-confirm')?.addEventListener('click', () => {
        const targetWaiter = (modalRoot.querySelector('#transfer-waiter-select') as HTMLSelectElement).value;
        restaurantMockAdapter.updateTableStatus(tenantId, table.id, {
          status: table.status,
          assignedWaiterName: targetWaiter,
        });
        showToast({
          title: 'Table Transferred',
          message: `${table.number} reassigned to ${targetWaiter}`,
          type: 'success',
        });
        close();
        onSuccess();
      });
    },
  });
}

export function openDirectPinSwitchModal(tenantId: string, userId: string, userName: string) {
  let pin = '';
  openModal({
    title: `Switch Station to ${escapeHtml(userName)}`,
    subtitle: 'Enter your personal 4-digit PIN to activate this workspace',
    width: 'sm',
    bodyHtml: `
      <div style="display:flex;flex-direction:column;align-items:center;padding:12px 0;">
        <div id="switch-pin-error" class="ess-form-error" style="display:none;margin-bottom:12px;width:100%;" role="alert"></div>
        <div class="ess-pin-dots" id="switch-dots">
          <span class="ess-pin-dot"></span>
          <span class="ess-pin-dot"></span>
          <span class="ess-pin-dot"></span>
          <span class="ess-pin-dot"></span>
        </div>
        <div class="ess-pin-keypad" id="switch-keypad">
          <button type="button" class="ess-pin-key" data-k="1">1</button>
          <button type="button" class="ess-pin-key" data-k="2">2</button>
          <button type="button" class="ess-pin-key" data-k="3">3</button>
          <button type="button" class="ess-pin-key" data-k="4">4</button>
          <button type="button" class="ess-pin-key" data-k="5">5</button>
          <button type="button" class="ess-pin-key" data-k="6">6</button>
          <button type="button" class="ess-pin-key" data-k="7">7</button>
          <button type="button" class="ess-pin-key" data-k="8">8</button>
          <button type="button" class="ess-pin-key" data-k="9">9</button>
          <button type="button" class="ess-pin-key ess-pin-key-action" data-k="back">&larr;</button>
          <button type="button" class="ess-pin-key" data-k="0">0</button>
          <button type="button" class="ess-pin-key ess-pin-key-enter" data-k="enter">&rarr;</button>
        </div>
      </div>
    `,
    footerHtml: `
      <button type="button" class="ess-btn ess-btn-secondary" id="switch-cancel">Cancel</button>
    `,
    onMount: (modalRoot, close) => {
      modalRoot.querySelector('#switch-cancel')?.addEventListener('click', close);

      const updateDots = (isErr = false) => {
        const dots = modalRoot.querySelectorAll('.ess-pin-dot');
        dots.forEach((dot, idx) => {
          dot.classList.remove('filled', 'error');
          if (isErr) dot.classList.add('error');
          else if (idx < pin.length) dot.classList.add('filled');
        });
      };

      const doSubmit = async () => {
        if (pin.length < 4) return;
        const errBox = modalRoot.querySelector('#switch-pin-error') as HTMLElement;
        if (errBox) errBox.style.display = 'none';

        try {
          await apiClient.staffPinLogin({
            tenantId,
            userId,
            pin,
          });
          showToast({ title: 'Welcome Back', message: `Signed in as ${userName}`, type: 'success' });
          close();
          window.location.reload();
        } catch (err: any) {
          updateDots(true);
          if (errBox) {
            errBox.textContent = err?.message || 'Incorrect PIN entered.';
            errBox.style.display = 'block';
          }
          setTimeout(() => {
            pin = '';
            updateDots(false);
          }, 700);
        }
      };

      modalRoot.querySelectorAll('.ess-pin-key').forEach((btn) => {
        btn.addEventListener('click', () => {
          const k = btn.getAttribute('data-k');
          if (k === 'back') {
            if (pin.length > 0) {
              pin = pin.slice(0, -1);
              updateDots();
            }
          } else if (k === 'enter') {
            if (pin.length >= 4) doSubmit();
          } else if (k && pin.length < 4) {
            pin += k;
            updateDots();
            if (pin.length === 4) doSubmit();
          }
        });
      });
    },
  });
}

export async function openFastSwitchStaffModal(tenantId: string) {
  let staffList: any[] = [];
  try {
    staffList = await apiClient.stationStaffList(tenantId);
  } catch {
    staffList = await apiClient.listStaff();
  }

  let selectedUserId = staffList[0]?.id || '';
  let selectedUserName = staffList[0] ? `${staffList[0].firstName} ${staffList[0].lastName}` : '';
  let pin = '';

  openModal({
    title: 'Fast Staff & Waiter Handover',
    subtitle: 'Select your profile and enter your 4-digit PIN to switch active workspace',
    width: 'md',
    bodyHtml: `
      <div class="ess-grid-2" style="min-height:360px;">
        <!-- Left: Staff List -->
        <div style="border-right:1px solid var(--ess-border);padding-right:16px;">
          <div style="font-size:13px;font-weight:700;margin-bottom:8px;color:var(--ess-text-primary);">Select Your Name:</div>
          <div class="ess-staff-list" style="max-height:300px;overflow-y:auto;">
            ${staffList
              .map((s, idx) => {
                const initials = (s.firstName?.[0] || 'S') + (s.lastName?.[0] || 'T');
                const isSel = idx === 0;
                return `
              <div class="ess-staff-card ${isSel ? 'selected' : ''}" data-switch-user="${escapeHtml(s.id)}" data-switch-name="${escapeHtml(s.firstName + ' ' + s.lastName)}">
                <div class="ess-staff-avatar">${escapeHtml(initials.toUpperCase())}</div>
                <div class="ess-staff-info">
                  <div class="ess-staff-name">${escapeHtml(s.firstName)} ${escapeHtml(s.lastName)}</div>
                  <div class="ess-staff-role">&bull; ${escapeHtml(s.primaryRole || s.roles?.[0] || 'STAFF')}</div>
                </div>
              </div>
            `;
              })
              .join('')}
          </div>
        </div>

        <!-- Right: PIN Pad -->
        <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;padding-left:16px;">
          <div style="font-size:12px;color:var(--ess-text-secondary);margin-bottom:2px;">Signing in as</div>
          <div id="fast-switch-target-name" style="font-size:16px;font-weight:700;margin-bottom:12px;color:var(--ess-text-primary);">
            ${escapeHtml(selectedUserName)}
          </div>
          <div id="fast-switch-error" class="ess-form-error" style="display:none;margin-bottom:10px;width:100%;max-width:240px;" role="alert"></div>
          <div class="ess-pin-dots">
            <span class="ess-pin-dot"></span>
            <span class="ess-pin-dot"></span>
            <span class="ess-pin-dot"></span>
            <span class="ess-pin-dot"></span>
          </div>
          <div class="ess-pin-keypad" style="grid-template-columns:repeat(3, 64px);gap:8px;">
            <button type="button" class="ess-pin-key" style="height:50px;font-size:18px;" data-fk="1">1</button>
            <button type="button" class="ess-pin-key" style="height:50px;font-size:18px;" data-fk="2">2</button>
            <button type="button" class="ess-pin-key" style="height:50px;font-size:18px;" data-fk="3">3</button>
            <button type="button" class="ess-pin-key" style="height:50px;font-size:18px;" data-fk="4">4</button>
            <button type="button" class="ess-pin-key" style="height:50px;font-size:18px;" data-fk="5">5</button>
            <button type="button" class="ess-pin-key" style="height:50px;font-size:18px;" data-fk="6">6</button>
            <button type="button" class="ess-pin-key" style="height:50px;font-size:18px;" data-fk="7">7</button>
            <button type="button" class="ess-pin-key" style="height:50px;font-size:18px;" data-fk="8">8</button>
            <button type="button" class="ess-pin-key" style="height:50px;font-size:18px;" data-fk="9">9</button>
            <button type="button" class="ess-pin-key ess-pin-key-action" style="height:50px;" data-fk="back">&larr;</button>
            <button type="button" class="ess-pin-key" style="height:50px;font-size:18px;" data-fk="0">0</button>
            <button type="button" class="ess-pin-key ess-pin-key-enter" style="height:50px;" data-fk="enter">&rarr;</button>
          </div>
        </div>
      </div>
    `,
    footerHtml: `
      <button type="button" class="ess-btn ess-btn-secondary" id="fast-switch-cancel">Cancel</button>
    `,
    onMount: (modalRoot, close) => {
      modalRoot.querySelector('#fast-switch-cancel')?.addEventListener('click', close);

      const updateDots = (isErr = false) => {
        const dots = modalRoot.querySelectorAll('.ess-pin-dot');
        dots.forEach((dot, idx) => {
          dot.classList.remove('filled', 'error');
          if (isErr) dot.classList.add('error');
          else if (idx < pin.length) dot.classList.add('filled');
        });
      };

      const doSubmit = async () => {
        if (!selectedUserId || pin.length < 4) return;
        const errBox = modalRoot.querySelector('#fast-switch-error') as HTMLElement;
        if (errBox) errBox.style.display = 'none';

        try {
          await apiClient.staffPinLogin({
            tenantId,
            userId: selectedUserId,
            pin,
          });
          showToast({ title: 'Workspace Switched', message: `Welcome, ${selectedUserName}!`, type: 'success' });
          close();
          window.location.reload();
        } catch (err: any) {
          updateDots(true);
          if (errBox) {
            errBox.textContent = err?.message || 'Incorrect PIN entered.';
            errBox.style.display = 'block';
          }
          setTimeout(() => {
            pin = '';
            updateDots(false);
          }, 700);
        }
      };

      modalRoot.querySelectorAll('[data-switch-user]').forEach((card) => {
        card.addEventListener('click', () => {
          modalRoot.querySelectorAll('[data-switch-user]').forEach((c) => c.classList.remove('selected'));
          card.classList.add('selected');
          selectedUserId = card.getAttribute('data-switch-user') || '';
          selectedUserName = card.getAttribute('data-switch-name') || '';
          const nameEl = modalRoot.querySelector('#fast-switch-target-name');
          if (nameEl) nameEl.textContent = selectedUserName;
          pin = '';
          updateDots();
        });
      });

      modalRoot.querySelectorAll('[data-fk]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const k = btn.getAttribute('data-fk');
          if (k === 'back') {
            if (pin.length > 0) {
              pin = pin.slice(0, -1);
              updateDots();
            }
          } else if (k === 'enter') {
            if (pin.length >= 4) doSubmit();
          } else if (k && pin.length < 4) {
            pin += k;
            updateDots();
            if (pin.length === 4) doSubmit();
          }
        });
      });
    },
  });
}

export function openLockStationModal(
  currentUser: any,
  tenantId: string,
  onUnlocked?: () => void
) {
  let pin = '';
  const fullName = currentUser ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() : 'Staff Member';
  const roles = currentUser?.roles ? currentUser.roles.map((r: any) => typeof r === 'string' ? r : r.role).join(', ') : 'Staff';

  openModal({
    title: '🔒 Station Locked',
    subtitle: `Locked for ${fullName} (${roles}). Enter your 4-digit PIN to resume.`,
    width: 'sm',
    bodyHtml: `
      <div style="display:flex;flex-direction:column;align-items:center;padding:12px 0;">
        <div style="width:64px;height:64px;border-radius:50%;background:var(--ess-primary-subtle,#e0f2fe);color:var(--ess-primary,#0284c7);display:flex;align-items:center;justify-content:center;font-size:24px;font-weight:700;margin-bottom:8px;">
          ${escapeHtml((currentUser?.firstName?.[0] || 'U') + (currentUser?.lastName?.[0] || ''))}
        </div>
        <div style="font-size:16px;font-weight:700;color:var(--ess-text-primary);">${escapeHtml(fullName)}</div>
        <div style="font-size:12px;color:var(--ess-text-secondary);margin-bottom:12px;">${escapeHtml(roles)} &bull; Private Session</div>

        <div id="lock-pin-error" class="ess-form-error" style="display:none;margin-bottom:10px;width:100%;max-width:240px;text-align:center;" role="alert"></div>

        <div class="ess-pin-dots" style="margin-bottom:16px;">
          <span class="ess-pin-dot"></span>
          <span class="ess-pin-dot"></span>
          <span class="ess-pin-dot"></span>
          <span class="ess-pin-dot"></span>
        </div>

        <div class="ess-pin-keypad" style="grid-template-columns:repeat(3, 68px);gap:10px;">
          <button type="button" class="ess-pin-key" style="height:50px;font-size:20px;font-weight:700;" data-lk="1">1</button>
          <button type="button" class="ess-pin-key" style="height:50px;font-size:20px;font-weight:700;" data-lk="2">2</button>
          <button type="button" class="ess-pin-key" style="height:50px;font-size:20px;font-weight:700;" data-lk="3">3</button>
          <button type="button" class="ess-pin-key" style="height:50px;font-size:20px;font-weight:700;" data-lk="4">4</button>
          <button type="button" class="ess-pin-key" style="height:50px;font-size:20px;font-weight:700;" data-lk="5">5</button>
          <button type="button" class="ess-pin-key" style="height:50px;font-size:20px;font-weight:700;" data-lk="6">6</button>
          <button type="button" class="ess-pin-key" style="height:50px;font-size:20px;font-weight:700;" data-lk="7">7</button>
          <button type="button" class="ess-pin-key" style="height:50px;font-size:20px;font-weight:700;" data-lk="8">8</button>
          <button type="button" class="ess-pin-key" style="height:50px;font-size:20px;font-weight:700;" data-lk="9">9</button>
          <button type="button" class="ess-pin-key ess-pin-key-action" style="height:50px;font-size:14px;font-weight:700;" data-lk="back">&larr; DEL</button>
          <button type="button" class="ess-pin-key" style="height:50px;font-size:20px;font-weight:700;" data-lk="0">0</button>
          <button type="button" class="ess-pin-key ess-pin-key-enter" style="height:50px;font-size:14px;font-weight:700;" data-lk="enter">UNLOCK</button>
        </div>
      </div>
    `,
    footerHtml: `
      <div style="display:flex;justify-content:space-between;width:100%;">
        <button type="button" class="ess-btn ess-btn-secondary" id="lock-switch-user-btn" style="font-size:12px;">👤 Switch User</button>
        <span style="font-size:11px;color:var(--ess-text-muted);display:flex;align-items:center;">Private Session Security</span>
      </div>
    `,
    onMount: (modalRoot, close) => {
      const closeX = modalRoot.querySelector('#ess-modal-close-x') as HTMLElement;
      if (closeX) closeX.style.display = 'none';

      const updateDots = (isErr = false) => {
        const dots = modalRoot.querySelectorAll('.ess-pin-dot');
        dots.forEach((dot, idx) => {
          dot.classList.remove('filled', 'error');
          if (isErr) dot.classList.add('error');
          else if (idx < pin.length) dot.classList.add('filled');
        });
      };

      const doUnlock = async () => {
        if (!currentUser || pin.length < 4) return;
        const errBox = modalRoot.querySelector('#lock-pin-error') as HTMLElement;
        if (errBox) errBox.style.display = 'none';

        try {
          await apiClient.staffPinLogin({
            tenantId,
            userId: currentUser.id,
            pin,
          });
          showToast({ title: 'Station Unlocked', message: `Welcome back, ${currentUser.firstName}!`, type: 'success' });
          window.removeEventListener('keydown', keyHandler);
          close();
          if (onUnlocked) onUnlocked();
        } catch (err: any) {
          updateDots(true);
          if (errBox) {
            errBox.textContent = err?.message || 'Incorrect PIN entered.';
            errBox.style.display = 'block';
          }
          setTimeout(() => {
            pin = '';
            updateDots(false);
          }, 700);
        }
      };

      modalRoot.querySelectorAll('[data-lk]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const k = btn.getAttribute('data-lk');
          if (k === 'back') {
            if (pin.length > 0) {
              pin = pin.slice(0, -1);
              updateDots();
            }
          } else if (k === 'enter') {
            if (pin.length >= 4) doUnlock();
          } else if (k && pin.length < 4) {
            pin += k;
            updateDots();
            if (pin.length === 4) doUnlock();
          }
        });
      });

      modalRoot.querySelector('#lock-switch-user-btn')?.addEventListener('click', () => {
        window.removeEventListener('keydown', keyHandler);
        close();
        openFastSwitchStaffModal(tenantId);
      });

      const keyHandler = (e: KeyboardEvent) => {
        if (e.key >= '0' && e.key <= '9') {
          if (pin.length < 4) {
            pin += e.key;
            updateDots();
            if (pin.length === 4) doUnlock();
          }
        } else if (e.key === 'Backspace') {
          pin = pin.slice(0, -1);
          updateDots();
        } else if (e.key === 'Enter') {
          if (pin.length >= 4) doUnlock();
        }
      };
      window.addEventListener('keydown', keyHandler);
    },
  });
}


